import React, { useEffect, useState, useRef } from "react";
import MDButton from "components/MDButton";
import config from "config/config";
import { googleSignIn } from "../_request";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "context";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import googleIcon from "../../../assets/images/google_icon.svg";

import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";
import { AnalyticsEvents } from "core/constant";
import CampaignTargetingModal from "components/Custom/CampaignTargetingModal";

function SignInWithGoogle() {
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginLoader, setLoginLoader] = useState(false);
  const [targetingModal, setTargetingModal] = useState({
    open: false,
    errorCode: null,
    retryState: null,
  });
  const { trackCustomEvent } = useAnalyticsEvent();
  const isProcessingRef = useRef(false);

  const googleLogin = (stateOverride) => {
    let googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${
      config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"].GOOGLE_CLIENT_ID
    }&redirect_uri=${
      config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"].GOOGLE_REDIRECT_URI
    }&response_type=code&scope=openid%20email%20profile&access_type=offline&prompt=consent`;

    if (stateOverride) {
      googleAuthUrl += `&state=${encodeURIComponent(JSON.stringify(stateOverride))}`;
    }
    window.location.href = googleAuthUrl;
  };

  const cleanUpOAuthParams = () => {
    const params = new URLSearchParams(location.search);
    params.delete("code");
    params.delete("state");
    params.delete("scope");
    params.delete("authuser");
    params.delete("prompt");
    params.delete("hd");
    const remaining = params.toString();
    navigate(
      { pathname: location.pathname, search: remaining ? `?${remaining}` : "" },
      { replace: true }
    );
  };

  const handleError = (errorData) => {
    if (errorData) console.error("Google sign-in failed:", errorData);
    showSnackbarWithMessage({ message: "Google sign-in failed. Please try again.", type: "error" });
    cleanUpOAuthParams();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");

    if (!code || isProcessingRef.current) return;
    isProcessingRef.current = true;

    const processGoogleSignIn = async () => {
      setLoginLoader(true);
      try {
        const stateParam = params.get("state");
        let stateData = {};
        try {
          stateData = stateParam ? JSON.parse(decodeURIComponent(stateParam)) : {};
        } catch (e) {
          console.error("Failed to parse Google OAuth state:", e);
        }

        const {
          data: { data, success, errorCode: responseErrorCode },
        } = await googleSignIn({
          code,
          userType: "CUSTOMER",
          ...(stateData.campaignCode && { campaignCode: stateData.campaignCode }),
          ...(stateData.restaurantStoreId && { restaurantStoreId: stateData.restaurantStoreId }),
          ...(stateData.brandId && { brandId: stateData.brandId }),
          ...(stateData.origin && { origin: stateData.origin }),
        });
        if (!success && responseErrorCode) {
          const retryState = {
            ...(stateData.gtmCode && { gtmCode: stateData.gtmCode }),
            ...(stateData.restaurantStoreId
              ? { restaurantStoreId: stateData.restaurantStoreId, origin: "LANDING" }
              : stateData.brandId
              ? { brandId: stateData.brandId, origin: "BRAND_LANDING" }
              : {}),
          };

          setTargetingModal({ open: true, errorCode: responseErrorCode, retryState });
          cleanUpOAuthParams();
          return;
        }
        if (!success) {
          handleError(data);
          return;
        }
        const verifiedCustomer = await saveAuthData(data?.tokens);
        if (!verifiedCustomer) {
          handleError();
          return;
        }
        const eventName = data?.isNewUser ? AnalyticsEvents.SIGN_UP : AnalyticsEvents.LOGIN;
        trackCustomEvent(
          eventName,
          {
            method: "google",
            auth_origin: "customer_portal",
            user_id: verifiedCustomer.id,
            user_role: verifiedCustomer.role,
            user_type: verifiedCustomer.userType,
          },
          false
        );

        navigate("/dashboard");
      } catch (error) {
        console.error("Google sign-in error:", error);
        handleError();
      } finally {
        setLoginLoader(false);
      }
    };

    processGoogleSignIn();
  }, [location.search]);

  return (
    <div>
      <MDButton
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={() => googleLogin()}
        sx={{
          borderRadius: "8px",
          py: 1.5,
          background: "#E8E8E8",
          borderColor: "#E8E8E8",
          marginTop: "1rem",
          color: "#475467",
          textTransform: "none",
          fontWeight: "500",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          "&:hover": {
            background: "#D1D1D1",
            borderColor: "#D1D5DB",
            color: "#101828",
          },
        }}
      >
        <img src={googleIcon} alt="Google" style={{ width: 22, height: 22 }} />
        Continue with Google
      </MDButton>

      {loginLoader && <SimpleBackdrop />}

      <CampaignTargetingModal
        open={targetingModal.open}
        onClose={() => setTargetingModal({ open: false, errorCode: null, retryState: null })}
        errorCode={targetingModal.errorCode}
        onContinue={() => googleLogin(targetingModal.retryState)}
      />
    </div>
  );
}

export default SignInWithGoogle;
